import React from 'react';
import { ButtonGroup } from '@emorg-prd/standard-react';
import { Toolbar, ToolbarItem } from '../common/components/Toolbar';
import { Table, Button, Select, ProgressBar, AlertVariantType, Icon } from '../common';
import { Searchbar } from '../common/components/Searchbar';
import WarningModal from '../common/modals/WarningModal';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import SubscriptionRequest from '../../models/entities/SubscriptionRequest';
import PortOpeningRequest from '../../models/entities/PortOpeningRequest';
import PortOpeningRequestService from '../../services/PortOpeningRequestService';
import SubscriptionRequestsService from '../../services/SubscriptionRequestsService';
import { LinkTreeItem } from '../common/LinkTree';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import Paginator from '../common/tables/Paginator';
import NewConnectedPORInfoModal from './components/NewConnectedPORInfoModal';
import './PortOpeningRequestsHome.css';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import CheckboxFilter, { CheckboxOption } from '../common/tables/CheckboxFilter';
import Filter from '../common/tables/Filter';
import serviceNow from '../../services/serviceNow.config';
import styled from 'styled-components';
import { Params, useNavigate, useParams } from 'react-router-dom';
import { WorkflowState } from '../../models/enums/WorkflowState';
import ProgressBarSmall from './components/ProgressBarSmall';
import moment from 'moment';
import Memoized from '../common/Memoized';
import DateRangePicker from '../common/DateRangePicker';
import '../common/CommonElements.css';
import { TextLink } from '../common/components/TextLinkNew';
import PortOpeningRequestsHomeTableRow from './PortOpeningRequestsHomeTableRow';

export type PORFilters = {
	scope: CheckboxOption[];
	status: CheckboxOption[];
	searchUser: string;
	subscriptionId: number;
	orderBy: string;
	fromDate: string;
	toDate: string;
};

const MyP = styled.p`
	margin-right: 8px;
	margin-bottom: auto !important;
	margin-top: auto !important;
`;

const StyledItem = styled(ToolbarItem)`
	width: 50%;
`;

const MyLink = styled.span`
	color: #0c69b0;
	text-decoration: underline;
	cursor: pointer;
`;

interface PortOpeningRequestsHomeProps extends PortOpeningRequestsHomeWithParamsProps {}

interface PortOpeningRequestsHomeWithParamsProps {
	scopes?: PortOpeningRequestsDirection[];
	subtitle?: string;
	navigate?: any;
	params?: Params<string>;
}

interface PortOpeningRequestsHomeWithParamsState {
	loading: boolean;
	subscription?: SubscriptionRequest;
	requests: PortOpeningRequest[];
	filteredRequests: PortOpeningRequest[];
	endModal: AlertBanner;
	newConnectedPORInfoModalVisible: boolean;

	visibleRequests: PortOpeningRequest[];
}

interface PORByRequestFilterProps {
	pors: PortOpeningRequest[];
	onFilterChange: any;
	subscriptionId: number;
	subscriptionOwner: string;
}

export type PORsMemoizedData = {
	searchUser: void;
	status: void;
	scope: void;
	orderBy: void;
	fromDate: void;
	toDate: void;
};

const defaultMemoizedDataStructure = {
	searchUser: undefined,
	orderBy: undefined,
	scope: undefined,
	status: undefined,
	fromDate: undefined,
	toDate: undefined,
} as PORsMemoizedData;

const memoized = new Memoized(defaultMemoizedDataStructure);

class PortOpeningRequestsHomeWithParams extends React.Component<
	PortOpeningRequestsHomeWithParamsProps,
	PortOpeningRequestsHomeWithParamsState
> {
	portOpeningRequestService: PortOpeningRequestService;

	subscriptionRequestsService: SubscriptionRequestsService;

	filter: Filter<PORFilters>;

	constructor(props) {
		super(props);
		this.portOpeningRequestService = new PortOpeningRequestService();
		this.subscriptionRequestsService = new SubscriptionRequestsService();
		this.state = {
			loading: true,
			requests: [],
			filteredRequests: [],
			visibleRequests: [],
			endModal: {
				visible: false,
			},
			newConnectedPORInfoModalVisible: false,
		};

		this.filter = new Filter(this.filterPORs, this.getFilters());
	}

	async componentDidMount() {
		this.fetchData();
	}

	fetchData = async () => {
		if (this.props.params.id) {
			const id = parseInt(this.props.params.id);
			const subscription = new SubscriptionRequest(await this.subscriptionRequestsService.getById(id));

			this.setState({
				subscription,
			});
		} else {
			this.setState({
				subscription: null,
			});
		}
		await this.loadRequests();
	};

	getLinkTree = (): LinkTreeItem[] => {
		return (
			(this.state.subscription?.id && [
				{ text: 'Subscriptions', to: '/subscriptions' },
				{
					text: this.state.subscription?.createdSubscriptionName,
					to: `/subscriptions/${this.state.subscription?.id}`,
				},
			]) ||
			[]
		);
	};

	loadRequests = async () => {
		try {
			let requests = [];
			this.setState({ loading: true });
			if (this.state.subscription?.id) {
				requests = await this.portOpeningRequestService.getBySubscriptionId(this.state.subscription?.id);
			} else {
				requests = await this.portOpeningRequestService.getMyRequests();
			}

			requests = requests
				.map((r) => new PortOpeningRequest(r))
				.reverse()
				.filter((request) => this.props.scopes.includes(request.direction));

			this.setState(
				{
					requests,
					loading: false,
				},
				this.filter.filter
			);
		} catch {
			this.setState({
				loading: false,
			});
			this.handleEndModal('Something went wrong, try again later.', 'error', 5000);
		}
	};

	handleEndModal = (text: string, variant: AlertVariantType, timeout: number) => {
		this.setState(
			{
				endModal: {
					visible: true,
					text,
					variant,
				},
			},
			() => {
				setTimeout(() => {
					this.redirectToSubscriptions();
				}, timeout);
			}
		);
	};

	redirectToSubscriptions = () => {
		this.setState(
			{
				endModal: { visible: false },
			},
			() => {
				this.props.navigate('/subscriptions');
			}
		);
	};

	getDefaultFilters = () => {
		const defaultFilters = {
			searchUser: '',
			status: [
				{ label: 'Submitted', value: 'Submitted', checked: true },
				{ label: 'Rejected', value: 'Rejected', checked: true },
				{ label: 'Pending Attestation', value: 'Pending Attestation', checked: true },
				{ label: 'Pending Business DOAG Approval', value: 'Pending Business DOAG Approval', checked: true },
				{
					label: 'Pending for Cloud Network Approval',
					value: 'Pending for Cloud Network Approval',
					checked: true,
				},
				{ label: 'Pending for Policy Team Approval', value: 'Pending for Policy Team Approval', checked: true },
				{
					label: WorkflowState.PendingSolutionArchitectureApproval.name,
					value: WorkflowState.PendingSolutionArchitectureApproval.name,
					checked: true,
				},
				{
					label: WorkflowState.PendingForCyberApproval.name,
					value: WorkflowState.PendingForCyberApproval.name,
					checked: true,
				},
				{
					label: WorkflowState.PendingApplicationApproval.name,
					value: WorkflowState.PendingApplicationApproval.name,
					checked: true,
				},
				{
					label: 'Pending subscription owner approval',
					value: 'Pending subscription owner approval',
					checked: true,
				},
				{ label: 'Review', value: 'Review', checked: true },
				{ label: 'Awaiting Implementation', value: 'Awaiting Implementation', checked: true },
				{ label: 'Completed', value: 'Completed', checked: true },
				{ label: 'Cancelled', value: 'Cancelled', checked: true },
				{ label: 'Draft', value: 'Draft', checked: true },
			],
			scope: [
				{
					label: 'Subscription <-> ExxonMobil Network',
					value: PortOpeningRequestsDirection.SubscriptionToOnPrem.name,
					checked: true,
				},
				{
					label: 'Subscription -> Internet',
					value: PortOpeningRequestsDirection.SubscriptionToInternet.name,
					checked: true,
				},
			],
			orderBy: 'newest',
			fromDate: undefined,
			toDate: undefined,
		} as PORFilters;

		return defaultFilters;
	};

	getFilters = () => {
		const defaultFilters = this.getDefaultFilters();

		const filters = {
			searchUser: memoized.getMemoizedData('searchUser', defaultFilters.searchUser),
			status: memoized.getMemoizedData('status', defaultFilters.status),
			scope: memoized.getMemoizedData('scope', defaultFilters.scope),
			orderBy: memoized.getMemoizedData('oderBy', defaultFilters.orderBy),
			fromDate: memoized.getMemoizedData('fromDate', defaultFilters.fromDate),
			toDate: memoized.getMemoizedData('toDate', defaultFilters.toDate),
		} as PORFilters;

		return filters;
	};

	allFiltersChecked = (key: string) => {
		return this.getFilters()[key].reduce((acc, box) => acc && box.checked, true);
	};

	getMemoizedData = (key: string, defaultData: any) => {
		return typeof memoized[key] !== 'undefined' ? memoized[key] : defaultData;
	};

	saveFilters = () => {
		memoized.saveMemoizeData('status', this.filter.filters.status);
		memoized.saveMemoizeData('searchUser', this.filter.filters.searchUser);
		memoized.saveMemoizeData('orderBy', this.filter.filters.orderBy);
		memoized.saveMemoizeData('scope', this.filter.filters.scope);
		memoized.saveMemoizeData('fromDate', this.filter.filters.fromDate);
		memoized.saveMemoizeData('toDate', this.filter.filters.toDate);
	};

	filterPORs = () => {
		let filteredRequests = [];

		this.saveFilters();

		const filterCallback = (por: PortOpeningRequest) => {
			return (
				this.filter.filterCheckBox(
					'status',
					por.workflowInstance.currentWorkflowInstanceState.workflowState?.name
				) &&
				this.filter.filterCheckBox('scope', por.direction.name) &&
				this.filter.isInDateRange('fromDate', 'toDate', new Date(por.workflowInstance.createdDate)) &&
				(this.filter.filterSearchText('searchUser', por.workflowInstance.createdBy.displayName) ||
					this.filter.filterSearchText('searchUser', por.contactUser?.displayName) ||
					this.filter.filterSearchText('searchUser', por.id.toString()))
			);
		};

		filteredRequests = this.state.requests.filter(filterCallback).sort((a, b) => {
			if (this.filter.filters.orderBy == undefined || this.filter.filters.orderBy === 'newest') {
				return a.id < b.id ? 1 : -1;
			}
			return a.id > b.id ? 1 : -1;
		});

		this.setState({
			filteredRequests,
		});
	};

	goToNewRequest = () => {
		this.props.navigate('/new');
	};

	goToPORDetails = (request) => {
		this.props.navigate(`/subscriptions/${request.subscriptionRequestId}/portOpeningRequests/${request.id}`);
	};

	getTicketURL = (ticket: string) => {
		return ticket.toUpperCase().includes('CRQ')
			? `https://itconsole.na.xom.com/#/${ticket}`
			: `https://${serviceNow.serviceNowEnv}.service-now.com/nav_to.do?uri=change_request.do?sysparm_query=number=${ticket}&sysparm_limit=1`;
	};

	clearSearchbar = (e) => {
		e.preventDefault();
		this.filter.updateFilter('', 'searchUser');
	};

	clearFilters = () => {
		memoized.resetAllMemoizeData();
		this.filter.updateFilters(this.getFilters());
	};

	render() {
		return (
			<>
				<SectionHomeWrapper
					title={`Port Opening Requests${this.props.subtitle ? `: ${this.props.subtitle}` : ''}`}
					linkTree={this.getLinkTree()}
				>
					<p>
						For more information about PORs{' '}
						<TextLink
							className="em-u-margin-right-none"
							external
							target="_blank"
							href="https://appwiki.xom.cloud/docs/ConnectedAzure/PortOpeningRequests.html"
						>
							Click here
						</TextLink>
					</p>
					{this.state.loading ? (
						<ProgressBar label="Loading requests..." className="em-u-margin-top-half" indeterminate />
					) : (
						<>
							<Table
								optionalClass="azure-por-home-table"
								header={
									<>
										<Toolbar>
											<ToolbarItem width="xl">
												<Searchbar
													condensed
													onClick={this.clearSearchbar}
													value={this.filter.filters.searchUser}
													onChange={(e) =>
														this.filter.handleSearchTextChange(e, 'searchUser')
													}
													placeholder={
														'Search all fields shown below (i.e. specific IP address, Description, etc.)'
													}
												/>
											</ToolbarItem>
											<ToolbarItem right>
												<Button
													variant="primary"
													size="small"
													onClick={() => {
														if (
															(this.props.params.id &&
																this.state.subscription?.canSubmitPortOpeningRequests()) ||
															!this.props.scopes.includes(
																PortOpeningRequestsDirection.SubscriptionToInternet
															) ||
															!this.props.scopes.includes(
																PortOpeningRequestsDirection.SubscriptionToOnPrem
															)
														) {
															this.goToNewRequest();
														} else {
															this.setState({
																newConnectedPORInfoModalVisible: true,
															});
														}
													}}
													disabled={
														this.state.subscription
															? !this.state.subscription?.canSubmitPortOpeningRequests()
															: false
													}
												>
													<Icon
														className="azure-por-button-icon"
														icon="plus-filled"
														size="small"
													/>
													<span>New Request</span>
												</Button>
											</ToolbarItem>
										</Toolbar>
										<Toolbar>
											<ToolbarItem>
												<DateRangePicker
													toDateOnChangeFunction={(e) =>
														this.filter.handleSearchTextChange(e, 'toDate')
													}
													fromDateOnChangeFunction={(e) =>
														this.filter.handleSearchTextChange(e, 'fromDate')
													}
													fromDateValue={this.filter.filters.fromDate}
													toDateValue={this.filter.filters.toDate}
													containerStyle={{ position: 'relative', bottom: '8px' }}
													pickerStyle={{ margin: '0px' }}
												/>
											</ToolbarItem>
											<ToolbarItem>
												<CheckboxFilter
													label="Scope"
													buttonProps={{ size: 'small' }}
													onChange={(event) =>
														this.filter.handleCheckboxChange(event, 'scope')
													}
													options={this.filter.filters.scope}
													allDefaultChecked={this.allFiltersChecked('scope')}
												/>
											</ToolbarItem>
											<ToolbarItem>
												<CheckboxFilter
													label="Status"
													onChange={(event) =>
														this.filter.handleCheckboxChange(event, 'status')
													}
													options={this.filter.filters.status}
													allDefaultChecked={this.allFiltersChecked('status')}
												/>
											</ToolbarItem>
											<ToolbarItem>
												<MyLink onClick={() => this.clearFilters()}>Clear filters</MyLink>
											</ToolbarItem>
											<ToolbarItem alignRight>
												<ButtonGroup>
													<MyP>Sort by</MyP>
													<Select
														//style={{ marginBottom: -12 }}
														value={this.filter.filters.orderBy}
														onChange={(e) =>
															this.filter.handleSearchTextChange(e, 'orderBy')
														}
														options={[
															{ value: 'newest', text: 'Newest' },
															{ value: 'oldest', text: 'Oldest' },
														]}
													/>
												</ButtonGroup>
											</ToolbarItem>
										</Toolbar>
									</>
								}
								footer={
									<Paginator
										data={this.state.filteredRequests}
										onPageChange={(page) => {
											this.setState({
												visibleRequests: page,
											});
										}}
									/>
								}
							>
								<Table.Head>
									<Table.Head.Row>
										<Table.Head.Cell> POR </Table.Head.Cell>
										<Table.Head.Cell> Scope </Table.Head.Cell>
										<Table.Head.Cell> Requestor </Table.Head.Cell>
										<Table.Head.Cell> Contact </Table.Head.Cell>
										<Table.Head.Cell> Approver </Table.Head.Cell>
										<Table.Head.Cell> Requested Date </Table.Head.Cell>
										<Table.Head.Cell> Status </Table.Head.Cell>
										<Table.Head.Cell> CRQ # </Table.Head.Cell>
									</Table.Head.Row>
								</Table.Head>
								<Table.Body>
									{this.state.visibleRequests.map((request) => (
										<PortOpeningRequestsHomeTableRow
											key={request.id}
											request={request}
											subOwnerDisplayName={this.state.subscription?.owner.displayName}
										/>
									))}
								</Table.Body>
							</Table>
						</>
					)}
				</SectionHomeWrapper>
				<WarningModal {...this.state.endModal} willTimeout={false} onClose={this.redirectToSubscriptions} />
				<NewConnectedPORInfoModal
					visible={this.state.newConnectedPORInfoModalVisible}
					onClose={() => this.setState({ newConnectedPORInfoModalVisible: false })}
				/>
			</>
		);
	}
}

const PortOpeningRequestsHome = (props: PortOpeningRequestsHomeProps) => {
	return (
		<PortOpeningRequestsHomeWithParams
			scopes={props.scopes}
			subtitle={props.subtitle}
			params={useParams()}
			navigate={useNavigate()}
		/>
	);
};

export default PortOpeningRequestsHome;
